class AzureGraphService {
  constructor(accessToken) {
    this.accessToken = accessToken;
    this.baseURL = 'https://graph.microsoft.com/beta';
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Authorization': `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
    }

    return response;
  }

  async getOnlineMeetings() {
    try {
      const response = await this.makeRequest('/me/onlineMeetings');
      const data = await response.json();
      return data.value || [];
    } catch (error) {
      console.error('Error fetching online meetings:', error);
      throw error;
    }
  }

  /**
   * Resolve a Teams meeting URL or numeric join ID to the Graph API online meeting ID.
   * Graph expects the full meeting id (e.g. "xxx_19:meeting_...@thread.v2"), not the number from meet/ links.
   * Tries: (1) filter by joinMeetingId (number from URL), (2) filter by JoinWebUrl (full URL).
   * @param {string} meetingUrlOrId - Full URL (e.g. https://teams.microsoft.com/meet/44456859126019?p=...) or numeric id
   * @returns {Promise<string>} - The online meeting id to use for /me/onlineMeetings/{id}/transcripts
   */
  async resolveOnlineMeetingId(meetingUrlOrId) {
    const input = (meetingUrlOrId || '').trim();
    if (!input) throw new Error('Meeting URL or ID is required');

    // Extract numeric join ID from URL if present (e.g. from .../meet/44456859126019?...)
    const meetMatch = input.match(/teams\.microsoft\.com\/meet\/(\d+)/i);
    const joinMeetingId = meetMatch ? meetMatch[1] : (input.match(/^\d+$/) ? input : null);

    // 1. Try by joinMeetingId (the number in the meet link)
    if (joinMeetingId) {
      try {
        const filter = `joinMeetingIdSettings/joinMeetingId eq '${joinMeetingId}'`;
        const encodedFilter = encodeURIComponent(filter);
        const response = await this.makeRequest(`/me/onlineMeetings?$filter=${encodedFilter}`);
        const data = await response.json();
        const meetings = data.value || [];
        if (meetings.length > 0 && meetings[0].id) {
          return meetings[0].id;
        }
      } catch (e) {
        console.warn('Resolve by joinMeetingId failed:', e.message);
      }
    }

    // 2. Try by JoinWebUrl (exact URL match; joinWebUrl must be URL-encoded in the filter per Microsoft docs)
    try {
      const urlToTry = input.startsWith('http') ? input : `https://teams.microsoft.com/meet/${input}`;
      const filter = `JoinWebUrl eq '${encodeURIComponent(urlToTry)}'`;
      const encodedFilter = encodeURIComponent(filter);
      const response = await this.makeRequest(`/me/onlineMeetings?$filter=${encodedFilter}`);
      const data = await response.json();
      const meetings = data.value || [];
      if (meetings.length > 0 && meetings[0].id) {
        return meetings[0].id;
      }
    } catch (e) {
      console.warn('Resolve by JoinWebUrl failed:', e.message);
    }

    throw new Error(
      'Could not resolve this meeting link to a Teams online meeting. ' +
      'Ensure you are the organizer or the meeting appears in your calendar, and that the link is a valid Teams meeting URL.'
    );
  }

  async listMeetingTranscripts(meetingId) {
    try {
      const response = await this.makeRequest(`/me/onlineMeetings/${meetingId}/transcripts`);
      const data = await response.json();
      return data.value || [];
    } catch (error) {
      console.error(`Error listing transcripts for meeting ${meetingId}:`, error);
      throw error;
    }
  }

  async getTranscriptContent(meetingId, transcriptId) {
    try {
      console.log('🔍 Fetching transcript content for:', { meetingId, transcriptId });

      // Graph transcript /content requires a specific Accept format; */* returns "Invalid format" error
      const response = await this.makeRequest(
        `/me/onlineMeetings/${meetingId}/transcripts/${transcriptId}/content`,
        { headers: { 'Accept': 'text/vtt' } }
      );
      const rawTranscript = await response.text();
      
      console.log('📄 RAW TRANSCRIPT FROM TEAMS:', rawTranscript);
      
      // Clean the transcript
      const cleanedTranscript = this.cleanTranscript(rawTranscript);
      console.log('✨ CLEANED TRANSCRIPT:', cleanedTranscript);
      
      return cleanedTranscript;
    } catch (error) {
      console.error(`Error fetching transcript content:`, error);
      throw error;
    }
  }

  cleanTranscript(rawTranscript) {
    if (!rawTranscript || typeof rawTranscript !== 'string') {
      return rawTranscript;
    }

    // Remove WEBVTT header
    let cleaned = rawTranscript.replace(/^WEBVTT\s*\n/i, '');
    
    // Remove timestamp lines (format: 00:00:03.771 --> 00:00:04.051)
    cleaned = cleaned.replace(/\d{2}:\d{2}:\d{2}\.\d{3}\s*-->\s*\d{2}:\d{2}:\d{2}\.\d{3}\s*\n/g, '');
    
    // Extract speaker content from <v Speaker>text</v> format
    cleaned = cleaned.replace(/<v ([^>]+)>([^<]+)<\/v>/g, '$1: $2');
    
    // Remove empty lines and extra whitespace
    cleaned = cleaned.replace(/\n\s*\n/g, '\n').trim();
    
    return cleaned;
  }
}

export default AzureGraphService;